import { useEffect, useState } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import { calculateDimensionsFromOutline } from '../../utils/dimensionCalculations';
import { calculatePolygonArea } from '../Editor/tools/PolygonTool';
import { gardenStorage } from '../../services/gardenStorage';
import WizardHeader from './WizardHeader';
import WizardFooter from './WizardFooter';
import Step1GardenBasics from './Step1GardenBasics';
import Step2BackgroundImage from './Step2BackgroundImage';
import Step3GardenOutline from './Step3GardenOutline';
import Step4HeightDifferences from './Step4HeightDifferences';
import ConfirmDialog from '../ui/ConfirmDialog';
import { AlertTriangle } from 'lucide-react';

/**
 * SetupWizard - Main wizard container component
 *
 * Features:
 * - 4-step wizard flow (Garden Basics → Background Image → Outline → Height Differences)
 * - Conditional step rendering based on currentStep
 * - Integration with WizardContext for state management
 * - Full-screen modal overlay with centered dialog
 *
 * Props:
 * - onComplete: Called when wizard is completed (user clicks "Fertigstellen" on step 4)
 *
 * Design:
 * - Full-screen overlay: bg-gray-900 with 90% opacity
 * - Dialog: White background, rounded corners, max-width 800px
 * - Font: Inter family
 */
const SetupWizard = ({ onComplete }) => {
  const {
    currentStep,
    nextStep,
    previousStep,
    validateStep,
    gardenData,
    validationErrors,
    updateBasics,
    updateBackgroundImage,
    updateOutline,
    updateHeightDifferences,
  } = useWizard();

  const { settings } = useGardenSettings();

  // State for duplicate name confirmation dialog
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateGardenId, setDuplicateGardenId] = useState(null);

  // Load existing settings on mount (for edit mode)
  useEffect(() => {
    // Check if we have existing settings and wizard is empty
    // Only pre-fill if user has completed settings before (has required fields)
    const hasExistingSettings = settings.length && settings.width && settings.soilType;
    const wizardIsEmpty = !gardenData.basics?.length;

    if (hasExistingSettings && wizardIsEmpty) {
      console.log('Pre-filling wizard with existing settings (edit mode)');

      // Pre-fill Step 1: Garden Basics
      updateBasics({
        length: settings.length,
        width: settings.width,
        soilType: settings.soilType,
        phValue: settings.phValue,
        drainage: settings.drainage,
        orientation: settings.orientation,
        sunlight: settings.sunlight,
        zipCode: settings.zipCode || '',
        city: settings.city || '',
        country: settings.country || '',
        hardinessZone: settings.hardinessZone || '',
      });

      // Pre-fill Step 2: Background Image (if exists)
      if (settings.backgroundImage?.dataUrl) {
        updateBackgroundImage({
          dataUrl: settings.backgroundImage.dataUrl,
          transparency: settings.backgroundImage.transparency ?? 50,
          position: settings.backgroundImage.position || { x: 20, y: 20 },
          rotation: settings.backgroundImage.rotation || 0,
          calibration: settings.backgroundImage.calibration || {
            scale: 100,
            isCalibrated: false,
            realDistance: 5,
            pixelDistance: null,
          },
          locked: settings.backgroundImage.locked ?? true,
          file: null, // Can't restore file object
        });
      }

      // Pre-fill Step 3: Garden Outline (if exists)
      if (settings.outline?.vertices?.length > 0) {
        updateOutline({
          vertices: settings.outline.vertices,
          isClosed: settings.outline.isClosed,
        });
      }

      // Pre-fill Step 4: Height Differences (if exists)
      if (settings.heightDifferences?.enabled && settings.heightDifferences?.zones?.length > 0) {
        updateHeightDifferences({
          enabled: true,
          zones: settings.heightDifferences.zones,
        });
      }

      // Pre-fill Step 4: Grundgefälle + legacy maxHeight (#624) — unabhängig
      // von den Zonen; maxHeight dient nur noch als Slider-Vorbelegung
      const slopePrefill = {
        ...(settings.heightDifferences?.baseSlope && {
          baseSlope: settings.heightDifferences.baseSlope,
        }),
        ...(settings.heightDifferences?.maxHeight > 0 && {
          maxHeight: settings.heightDifferences.maxHeight,
        }),
      };
      if (Object.keys(slopePrefill).length > 0) {
        updateHeightDifferences(slopePrefill);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount to pre-fill wizard with existing settings (edit mode)

  // Step metadata (titles for each step)
  const stepMetadata = {
    1: { title: 'Garten-Grundlagen' },
    2: { title: 'Hintergrundbild (Optional)' },
    3: { title: 'Garten-Umriss zeichnen' },
    4: { title: 'Höhenunterschiede (Optional)' },
  };

  const currentStepMeta = stepMetadata[currentStep] || { title: '', canSkip: false };

  /**
   * Check for duplicate garden name and show confirmation if found
   */
  const checkDuplicateAndComplete = () => {
    const gardenName = (gardenData.basics?.name || 'Mein Garten').trim();

    // Check if name already exists in localStorage
    const existingGarden = gardenStorage.findByName(gardenName);

    if (existingGarden) {
      // Found duplicate - show confirmation dialog
      setDuplicateGardenId(existingGarden.id);
      setShowDuplicateConfirm(true);
    } else {
      // No duplicate - complete wizard directly
      completeWizard();
    }
  };

  /**
   * Complete wizard and call onComplete callback
   */
  const completeWizard = () => {
    console.log('Wizard completed with data:', gardenData);
    if (onComplete) {
      onComplete(gardenData);
    }
  };

  /**
   * Handle user confirmation to replace duplicate garden
   */
  const handleConfirmReplace = () => {
    setShowDuplicateConfirm(false);

    // Note: The actual deletion will be handled by the parent component (MainLayout)
    // that receives the gardenData and will delete the old garden before creating new one
    console.log(`User confirmed to replace garden with ID: ${duplicateGardenId}`);

    // Store the duplicate ID in gardenData so parent can handle deletion
    const gardenDataWithDuplicateId = {
      ...gardenData,
      replacingGardenId: duplicateGardenId,
    };

    if (onComplete) {
      onComplete(gardenDataWithDuplicateId);
    }
  };

  /**
   * Handle user cancellation of duplicate replacement
   */
  const handleCancelReplace = () => {
    setShowDuplicateConfirm(false);
    setDuplicateGardenId(null);
    // Stay in wizard - user can change the name or cancel
  };

  // Handle Next button click
  const handleNext = () => {
    // Validate current step (skip validation for optional steps 2 and 4)
    if (currentStep !== 2 && currentStep !== 4) {
      const isValid = validateStep(currentStep);

      if (!isValid) {
        console.log('Validation failed for step', currentStep, validationErrors);
        return;
      }
    }

    // If leaving Step 3, calculate and store dimensions in outline
    if (currentStep === 3 && gardenData.outline?.vertices && gardenData.outline.vertices.length >= 3) {
      const calibrationScale = gardenData.backgroundImage?.calibration?.scale;
      const calculatedDimensions = calculateDimensionsFromOutline(
        gardenData.outline.vertices,
        calibrationScale,
        calculatePolygonArea
      );

      // Update outline with calculated dimensions
      updateOutline({
        ...gardenData.outline,
        calculatedDimensions
      });
    }

    // Check if this is the last step
    if (currentStep === 4) {
      // Check for duplicate garden name before completing
      checkDuplicateAndComplete();
    } else {
      // Move to next step
      nextStep();
    }
  };

  // Handle Back button click
  const handleBack = () => {
    if (currentStep > 1) {
      previousStep();
    }
  };

  // Check if current step is valid (enables/disables Next button)
  const isStepValid = () => {
    // Steps 2 and 4 are optional, always valid
    if (currentStep === 2 || currentStep === 4) {
      return true;
    }

    // For step 1, check if ALL required fields are filled
    if (currentStep === 1) {
      const { soilType, phValue, drainage, orientation, zipCode, city } = gardenData.basics;
      return (
        soilType !== null &&
        phValue !== null &&
        drainage !== null &&
        orientation !== null &&
        zipCode !== null &&
        zipCode.trim() !== '' &&
        city !== null
      );
    }

    // For step 3, check if outline is complete
    if (currentStep === 3) {
      const { vertices, isClosed, skipped } = gardenData.outline;
      // Mobile path: skipped=true means simplified mobile rect or explicit skip
      if (skipped) return true;
      // Desktop path: polygon must be drawn and closed
      return vertices.length >= 3 && isClosed;
    }

    // Default: no validation errors
    return Object.keys(validationErrors).length === 0;
  };

  // Dynamically adjust dialog size for canvas-heavy steps.
  // Steps 2/3/4 share the wide layout so the responsive canvas in Step 2
  // (ScaleCalibrator) and Step 3 (outline) get enough horizontal room.
  const dialogMaxWidth = currentStep >= 2 ? 'max-w-[1400px]' : 'max-w-[800px]';
  // Use a taller dialog for canvas steps so the responsive canvas has more
  // vertical headroom on typical 1280×720/768 laptop viewports.
  const dialogMaxHeight = currentStep >= 2 ? 'max-h-[95vh]' : 'max-h-[90vh]';

  return (
    <>
      <div
        className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-center justify-center z-50 font-inter"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wizard-title"
        data-testid="setup-wizard-dialog"
      >
        {/* Dialog Container */}
        <div className={`bg-white rounded-lg shadow-2xl ${dialogMaxWidth} w-full mx-4 ${dialogMaxHeight} flex flex-col`}>
          {/* Header with Progress */}
          <WizardHeader currentStep={currentStep} stepTitle={currentStepMeta.title} />

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {currentStep === 1 && <Step1GardenBasics />}
            {currentStep === 2 && <Step2BackgroundImage />}
            {currentStep === 3 && <Step3GardenOutline />}
            {currentStep === 4 && (
              <Step4HeightDifferences northAngle={settings.northAngle ?? null} />
            )}
          </div>

          {/* Footer with Navigation Buttons */}
          <WizardFooter
            currentStep={currentStep}
            onBack={handleBack}
            onNext={handleNext}
            isStepValid={isStepValid()}
            isLastStep={currentStep === 4}
          />
        </div>
      </div>

      {/* Duplicate Name Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDuplicateConfirm}
        onConfirm={handleConfirmReplace}
        onCancel={handleCancelReplace}
        title="Garten ersetzen?"
        message={`Ein Garten mit dem Namen "${gardenData.basics?.name || 'Mein Garten'}" existiert bereits auf diesem Gerät. Möchten Sie den vorherigen Garten ersetzen?`}
        confirmLabel="Ersetzen"
        cancelLabel="Abbrechen"
        variant="danger"
        icon={AlertTriangle}
      />
    </>
  );
};

export default SetupWizard;
